import React from 'react';
import { makeAutoObservable } from 'mobx';
import { ViewType, WithdrawalActionType, WithdrawalState } from 'src/AppState/WithdrawalState';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { Amount } from 'src_common/common/amount/Amount';
import addError from 'src/utils/error.jsx';
import { PermissionGrantsState } from 'src/AppState/PermissionGrants/PermissionGrants.state';
import { Permission } from 'src/AppState/PermissionGrants/PermissionGrants.types';

interface ViewButtonType {
    action: WithdrawalActionType;
    name: string;
}

const ACTIONS: Record<ViewType, Array<ViewButtonType>> = {
    your: [
        { action: 'complete', name: 'Complete withdrawal' },
        { action: 'cancel', name: 'Cancel withdrawal' },
        { action: 'reject', name: 'Reject withdrawal' },
        { action: 'unassign', name: 'Unassign' },
    ],
    none: [
        { action: 'cancel', name: 'Cancel withdrawal' },
        { action: 'assign', name: 'Assign to me' },
    ],
    other: [
        { action: 'cancel', name: 'Cancel withdrawal' },
        { action: 'assign', name: 'Assign to me' },
    ],
};

export interface OptionType {
    value: string | undefined;
    selected: boolean;
    label: string;
}

const getConfirmMessage = (
    selectedAction: WithdrawalActionType,
    isLimitReached: boolean,
    limitValue: Amount
): string | null => {
    const confirmMessage: Record<WithdrawalActionType, string | null> = {
        cancel: 'Are you sure you want to cancel this withdrawal',
        reject: 'Are you sure you want to reject this withdrawal',
        complete: null,
        assign: null,
        unassign: null,
    };

    if (selectedAction === 'complete' && isLimitReached) {
        return `Are you sure you want to complete this withdrawal as it’s over ${limitValue.format('GBP')}?`;
    }

    return confirmMessage[selectedAction];
};

const convertToActionType = (value: string): WithdrawalActionType | null => {
    if (
        value === 'cancel' ||
        value === 'reject' ||
        value === 'complete' ||
        value === 'assign' ||
        value === 'unassign'
    ) {
        return value;
    }

    return null;
};

interface PendingActionType {
    type: WithdrawalActionType;
    actionInProgress: boolean;
    id: number;
    confirmationMessage: string | null;
}

export class WithdrawalRequestsRowState {
    private readonly withdrawalState: WithdrawalState;
    private readonly configState: ConfigState;
    private readonly permissionGrantsState: PermissionGrantsState;
    private readonly getType: () => ViewType;
    private readonly getWithdrawalId: () => number;
    public showConfirm: PendingActionType | null = null;

    public constructor(
        withdrawalState: WithdrawalState,
        configState: ConfigState,
        permissionGrantsState: PermissionGrantsState,
        getType: () => ViewType,
        getWithdrawalId: () => number
    ) {
        this.withdrawalState = withdrawalState;
        this.configState = configState;
        this.permissionGrantsState = permissionGrantsState;
        this.getType = getType;
        this.getWithdrawalId = getWithdrawalId;
        makeAutoObservable(this);
    }

    public async execAction(withdrawalId: number, mode: WithdrawalActionType): Promise<void> {
        if (
            (mode === 'assign' || mode === 'complete') &&
            this.isLimitReached &&
            this.permissionGrantsState.has(Permission.bankingWithdrawalsNoLimit) === false
        ) {
            addError(
                `You don't have permission to perform this action on withdrawals over ${this.withdrawalStandardLimit.format('GBP')}`
            );
        } else {
            await this.withdrawalState.execAction(withdrawalId, mode);
        }
    }

    public onChange = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        if (this.showConfirm !== null) {
            return;
        }

        const selectedValue = e.currentTarget.value;
        const selectedAction = convertToActionType(selectedValue);
        if (selectedAction === null) {
            console.error(`option not supported: ${selectedValue}`);
            return;
        }

        const actionMessage = getConfirmMessage(selectedAction, this.isLimitReached, this.withdrawalStandardLimit);
        const withdrawalId = this.getWithdrawalId();

        if (actionMessage === null) {
            this.showConfirm = {
                type: selectedAction,
                id: withdrawalId,
                actionInProgress: true,
                confirmationMessage: actionMessage,
            };

            await this.execAction(withdrawalId, selectedAction);
            this.showConfirm = null;
            return;
        }

        this.showConfirm = {
            type: selectedAction,
            id: withdrawalId,
            actionInProgress: false,
            confirmationMessage: actionMessage,
        };
    };

    public onConfirm = async (): Promise<void> => {
        if (this.showConfirm === null) {
            return;
        }

        if (this.showConfirm.actionInProgress === false) {
            this.showConfirm.actionInProgress = true;
            await this.execAction(this.showConfirm.id, this.showConfirm.type);
            this.showConfirm = null;
            return;
        }
    };

    public onReject = (): void => {
        this.showConfirm = null;
    };

    private getSelectValue(value: WithdrawalActionType | null): boolean {
        if (this.showConfirm === null) {
            return false;
        }

        return this.showConfirm.type === value;
    }

    private createOption = (viewButton: ViewButtonType): OptionType => {
        return {
            value: viewButton.action,
            selected: this.getSelectValue(viewButton.action),
            label: viewButton.name,
        };
    };

    public get optionList(): Array<OptionType> {
        const type = this.getType();
        const buttons = ACTIONS[type];
        return buttons.map(this.createOption);
    }

    public get selectIsDisabled(): boolean {
        if (this.showConfirm === null) {
            return false;
        }

        return this.showConfirm.actionInProgress;
    }

    public get currentSelectedValue(): string {
        if (this.showConfirm === null) {
            return '';
        }
        return this.showConfirm.type;
    }

    public get confirmationMessage(): string | null {
        const action = this.showConfirm;

        if (action === null) {
            return null;
        }

        if (action.actionInProgress === true) {
            return null;
        }

        return action.confirmationMessage;
    }

    private get withdrawalAmount(): Amount | null {
        const withdrawalData = this.withdrawalState.getItem(this.getType(), this.getWithdrawalId());

        if (withdrawalData === null) {
            return null;
        }

        const amount = this.configState.precision.newFromAnything(withdrawalData.amount);

        return amount;
    }

    private get withdrawalStandardLimit(): Amount {
        return new Amount(this.configState.config.withdrawalStandardLimit);
    }

    private get isLimitReached(): boolean {
        if (this.withdrawalAmount === null) {
            console.error('Amount is not set');
            return false;
        }
        return this.withdrawalAmount.isGreaterThan(this.withdrawalStandardLimit);
    }
}
